import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { api, formatErrorDetail } from "@/lib/api";
import { COLORS, SHIPPING_OPTIONS, PRODUCT, BRAND } from "@/data/product";
import { Loader2, Plus, Minus, Trash2, Copy, ExternalLink, MessageCircle, Send, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

function formatEUR(n) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
}

const PAYMENT_METHODS = [
  { id: "cash", label: "Espèces" },
  { id: "transfer", label: "Virement" },
  { id: "paypal", label: "PayPal" },
  { id: "manual", label: "Autre" },
];

export default function WhatsAppOrderDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("form"); // 'form' | 'link-result'
  const [submitting, setSubmitting] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);

  // form fields
  const [email, setEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [items, setItems] = useState([]); // [{colorId,colorName,colorCode,quantity}]
  const [shipping, setShipping] = useState("clickcollect");
  const [paymentStatus, setPaymentStatus] = useState("paid");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [colorPick, setColorPick] = useState("");
  const [search, setSearch] = useState("");

  const filteredColors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COLORS;
    return COLORS.filter((c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }, [search]);

  const shippingOpt = SHIPPING_OPTIONS.find((s) => s.id === shipping) || SHIPPING_OPTIONS[0];
  const subtotal = items.reduce((s, i) => s + i.quantity * PRODUCT.price, 0);
  const total = subtotal + shippingOpt.price;

  const addColor = () => {
    if (!colorPick) return;
    const c = COLORS.find((x) => x.id === colorPick);
    if (!c) return;
    setItems((prev) => {
      const existing = prev.find((i) => i.colorId === c.id);
      if (existing) {
        return prev.map((i) => (i.colorId === c.id ? { ...i, quantity: i.quantity + 1 } : i));
      }
      return [...prev, { colorId: c.id, colorName: c.name, colorCode: c.code, swatch: c.swatch, quantity: 1 }];
    });
    setColorPick("");
    setSearch("");
  };

  const setQty = (id, q) =>
    setItems((prev) => prev.map((i) => (i.colorId === id ? { ...i, quantity: Math.max(1, Math.min(50, q)) } : i)));
  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.colorId !== id));

  const reset = () => {
    setStep("form");
    setSubmitting(false);
    setPaymentLink(null);
    setEmail("");
    setCustomerName("");
    setItems([]);
    setShipping("clickcollect");
    setPaymentStatus("paid");
    setPaymentMethod("cash");
    setNote("");
    setColorPick("");
    setSearch("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (items.length === 0) {
      toast.error("Ajoutez au moins une couleur.");
      return;
    }
    if (!email) {
      toast.error("Email du client requis.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/orders/whatsapp", {
        email,
        customer_name: customerName || null,
        items: items.map((i) => ({
          colorId: i.colorId,
          colorName: i.colorName,
          colorCode: i.colorCode,
          length: "24\"",
          quantity: i.quantity,
        })),
        shipping,
        payment_status: paymentStatus,
        payment_method: paymentMethod,
        note: note || null,
      });
      toast.success(`Commande ${data.shopify_order_name} créée dans Shopify`);
      onCreated?.();
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Échec de création");
    } finally {
      setSubmitting(false);
    }
  };

  const generatePaymentLink = async () => {
    if (items.length === 0) {
      toast.error("Ajoutez au moins une couleur.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/orders/payment-link", {
        items: items.map((i) => ({
          colorId: i.colorId,
          colorName: i.colorName,
          colorCode: i.colorCode,
          length: "24\"",
          quantity: i.quantity,
        })),
        shipping,
        email: email || null,
        origin_url: window.location.origin,
      });
      setPaymentLink(data);
      setStep("link-result");
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Échec");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!paymentLink?.url) return;
    await navigator.clipboard.writeText(paymentLink.url);
    toast.success("Lien copié dans le presse-papier");
  };

  const sendToWhatsApp = () => {
    if (!paymentLink?.url) return;
    const message = encodeURIComponent(
      `Bonjour ! Voici votre lien de paiement sécurisé pour votre commande French Curl Vibes :\n\n${paymentLink.url}\n\nMontant : ${formatEUR(paymentLink.total)}\n\nMerci !`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-[#25D366] hover:bg-[#1EAB53] text-white h-10 px-5" data-testid="open-wa-order-btn">
          <MessageCircle className="h-4 w-4 mr-2" />
          Nouvelle commande WhatsApp
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#FDFBF9] border-[#EADBD1] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wa-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#2C2420]">
            {step === "link-result" ? "Lien de paiement Stripe" : "Nouvelle commande WhatsApp"}
          </DialogTitle>
        </DialogHeader>

        {step === "form" && (
          <form onSubmit={submit} className="space-y-5 mt-2">
            {/* Client */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Email client *" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required testid="wa-email" />
              <FormField label="Nom du client" value={customerName} onChange={(e) => setCustomerName(e.target.value)} testid="wa-name" />
            </div>

            {/* Color picker */}
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-[#6B5D54] mb-1.5 block">Ajouter une couleur</label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    list="wa-colors"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      const match = COLORS.find((c) => `${c.name} (${c.code})` === e.target.value);
                      if (match) setColorPick(match.id);
                    }}
                    placeholder="Tapez nom ou code (1B, 27, miel…)"
                    className="w-full h-11 px-4 rounded-xl bg-white border border-[#EADBD1] focus:border-[#C58C85] focus:outline-none text-sm"
                    data-testid="wa-color-search"
                  />
                  <datalist id="wa-colors">
                    {filteredColors.slice(0, 50).map((c) => (
                      <option key={c.id} value={`${c.name} (${c.code})`} />
                    ))}
                  </datalist>
                </div>
                <Button type="button" onClick={addColor} variant="outline" className="rounded-xl border-[#2C2420] h-11" data-testid="wa-add-color">
                  <Plus className="h-4 w-4 mr-1" /> Ajouter
                </Button>
              </div>
            </div>

            {items.length > 0 && (
              <div className="space-y-2" data-testid="wa-items">
                {items.map((i) => (
                  <div key={i.colorId} className="flex items-center gap-3 rounded-xl bg-white border border-[#EADBD1] p-3" data-testid={`wa-item-${i.colorId}`}>
                    <div className="h-10 w-10 rounded-lg border border-[#EADBD1] flex-shrink-0" style={{ background: i.swatch }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#2C2420] truncate">{i.colorName}</p>
                      <p className="text-[11px] text-[#6B5D54] font-mono">{i.colorCode}</p>
                    </div>
                    <div className="flex items-center gap-1 rounded-full border border-[#EADBD1] px-1.5">
                      <button type="button" onClick={() => setQty(i.colorId, i.quantity - 1)} className="h-7 w-7 grid place-items-center hover:bg-[#F5EFE6] rounded-full">
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="w-5 text-center text-xs">{i.quantity}</span>
                      <button type="button" onClick={() => setQty(i.colorId, i.quantity + 1)} className="h-7 w-7 grid place-items-center hover:bg-[#F5EFE6] rounded-full">
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <span className="text-sm font-medium text-[#2C2420] w-16 text-right">{formatEUR(i.quantity * PRODUCT.price)}</span>
                    <button type="button" onClick={() => removeItem(i.colorId)} className="text-[#6B5D54] hover:text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Shipping */}
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-[#6B5D54] mb-1.5 block">Livraison</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {SHIPPING_OPTIONS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setShipping(s.id)}
                    className={`rounded-xl border p-2.5 text-left text-xs transition-colors ${
                      shipping === s.id ? "border-[#2C2420] bg-[#2C2420] text-white" : "border-[#EADBD1] bg-white hover:border-[#C58C85]"
                    }`}
                    data-testid={`wa-shipping-${s.id}`}
                  >
                    <p className="font-medium">{s.label}</p>
                    <p className={`text-[10px] mt-0.5 ${shipping === s.id ? "text-white/70" : "text-[#6B5D54]"}`}>
                      {s.price === 0 ? "Gratuit" : formatEUR(s.price)}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Payment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.22em] text-[#6B5D54] mb-1.5 block">Statut paiement</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentStatus("paid")}
                    className={`flex-1 h-10 rounded-xl border text-sm font-medium ${
                      paymentStatus === "paid" ? "bg-[#25D366] text-white border-[#25D366]" : "bg-white border-[#EADBD1] text-[#2C2420]"
                    }`}
                    data-testid="wa-status-paid"
                  >
                    Déjà payée
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentStatus("pending")}
                    className={`flex-1 h-10 rounded-xl border text-sm font-medium ${
                      paymentStatus === "pending" ? "bg-[#2C2420] text-white border-[#2C2420]" : "bg-white border-[#EADBD1] text-[#2C2420]"
                    }`}
                    data-testid="wa-status-pending"
                  >
                    En attente
                  </button>
                </div>
              </div>
              {paymentStatus === "paid" && (
                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-[#6B5D54] mb-1.5 block">Mode de paiement</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl bg-white border border-[#EADBD1] focus:border-[#C58C85] focus:outline-none text-sm"
                    data-testid="wa-payment-method"
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-[#6B5D54] mb-1.5 block">Note (optionnel)</label>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Ex: remise spéciale, conditions de retrait…"
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#EADBD1] focus:border-[#C58C85] focus:outline-none text-sm resize-none"
                data-testid="wa-note"
              />
            </div>

            <Separator className="bg-[#EADBD1]" />

            {/* Total */}
            <div className="rounded-2xl bg-[#F5EFE6] p-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-[#6B5D54]"><span>Sous-total</span><span>{formatEUR(subtotal)}</span></div>
              <div className="flex justify-between text-[#6B5D54]"><span>{shippingOpt.label}</span><span>{shippingOpt.price === 0 ? "Gratuit" : formatEUR(shippingOpt.price)}</span></div>
              <div className="flex justify-between text-[#2C2420] font-medium pt-1.5 border-t border-[#EADBD1]">
                <span>Total</span>
                <span className="font-serif text-lg" data-testid="wa-total">{formatEUR(total)}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <Button
                type="button"
                onClick={generatePaymentLink}
                disabled={submitting || items.length === 0}
                variant="outline"
                className="rounded-full border-[#2C2420] h-12"
                data-testid="wa-generate-link-btn"
              >
                <Send className="h-4 w-4 mr-2" /> Demander paiement Stripe
              </Button>
              <Button
                type="submit"
                disabled={submitting || items.length === 0 || !email}
                className="rounded-full bg-[#2C2420] hover:bg-[#3a2f2a] text-white h-12"
                data-testid="wa-create-order-btn"
              >
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Création…</> : <>Créer dans Shopify</>}
              </Button>
            </div>
          </form>
        )}

        {step === "link-result" && paymentLink && (
          <div className="space-y-5 mt-2" data-testid="wa-link-result">
            <div className="rounded-2xl bg-[#25D366]/10 border border-[#25D366]/30 p-5 text-center">
              <CheckCircle2 className="h-10 w-10 text-[#25D366] mx-auto" strokeWidth={1.5} />
              <p className="font-medium text-[#2C2420] mt-3">Lien de paiement généré</p>
              <p className="text-sm text-[#6B5D54] mt-1">Montant : <strong>{formatEUR(paymentLink.total)}</strong></p>
            </div>
            <div className="rounded-xl bg-white border border-[#EADBD1] p-3 text-xs font-mono break-all" data-testid="wa-link-url">
              {paymentLink.url}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button onClick={copyLink} variant="outline" className="rounded-full border-[#2C2420] h-12" data-testid="wa-link-copy-btn">
                <Copy className="h-4 w-4 mr-2" /> Copier le lien
              </Button>
              <Button onClick={sendToWhatsApp} className="rounded-full bg-[#25D366] hover:bg-[#1EAB53] text-white h-12" data-testid="wa-link-send-btn">
                <MessageCircle className="h-4 w-4 mr-2" /> Envoyer via WhatsApp
              </Button>
            </div>
            <p className="text-xs text-[#6B5D54] text-center">
              Quand le client paiera, la commande sera automatiquement créée dans Shopify et apparaîtra ici.
            </p>
            <Button
              variant="ghost"
              onClick={() => { setStep("form"); setPaymentLink(null); }}
              className="w-full text-[#6B5D54]"
              data-testid="wa-link-back-btn"
            >
              <X className="h-4 w-4 mr-2" /> Fermer
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.22em] text-[#6B5D54] mb-1.5 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full h-11 px-4 rounded-xl bg-white border border-[#EADBD1] focus:border-[#C58C85] focus:outline-none text-sm"
        data-testid={testid}
      />
    </div>
  );
}
